/**
 * The catalog's file picker for work the AI does on files (create from files, refine with AI): the materials upload
 * (with its refusals per file), then each landed file's text status, asked again every 2 s and announced as it
 * changes. A file without readable text cannot be read by the AI: remove it to go on.
 */

import type { TextStatus } from '@/api/types/materials';
import { AttachmentPicker } from '@/ui/Attachments';

import type { useMaterialUploads } from '../materials/useMaterialUploads';
import './files.css';

export type MaterialUploads = ReturnType<typeof useMaterialUploads>;

const STATUS_TEXT: Record<TextStatus, string> = {
  pending: 'Reading the text…',
  ready: 'Text ready',
  unreadable: 'No readable text: the AI cannot read it. Remove it above to go on.',
};

/** Why the files cannot be sent yet (still uploading or reading, or unreadable), or null. */
export function filesProblem(statuses: TextStatus[], busy: boolean): string | null {
  if (busy || statuses.includes('pending')) return 'Wait until every file is read.';
  if (statuses.includes('unreadable')) return 'Remove the files without readable text.';
  return null;
}

export function FilesPicker({ uploads, id, label = 'Choose files' }: { uploads: MaterialUploads; id: string; label?: string }) {
  const { draft, landed, statusOf } = uploads;
  return (
    <>
      <AttachmentPicker draft={draft} id={id} label={label} layout="table" />
      {landed.length > 0 && (
        <ul className="extraction-files" aria-label="Text of the uploaded files">
          {landed.map(({ key, file }) => {
            const status = statusOf(file);
            return (
              <li key={key}>
                <b>{file.filename}</b>{' '}
                <span className={status === 'unreadable' ? 'attach-error' : 'hint'} aria-live="polite">{STATUS_TEXT[status]}</span>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}
